import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../../services/api';

const LogViewer = ({ connectionId }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const [activeTab, setActiveTab] = useState('rawLogs'); // Default to 'rawLogs'
  const [searchTerm, setSearchTerm] = useState('');
  const eventSourceRef = useRef(null);
  const logsEndRef = useRef(null);
  const connectionIdRef = useRef(connectionId);

  // Connect to log stream only if connectionId changes or not previously connected
  useEffect(() => {
    // Only reconnect if connectionId has changed or we don't have a connection
    if (connectionId && (connectionId !== connectionIdRef.current || !eventSourceRef.current)) {
      
      const connectionChanged = connectionId && connectionIdRef.current && connectionId !== connectionIdRef.current;

      connectionIdRef.current = connectionId;

      if(connectionChanged){
        connectToLogStream(true); 
      }else{
        connectToLogStream(false); 
      }
      
    }
    
    return () => {
      // Only cleanup on component unmount, not on toggling visibility
      if (eventSourceRef.current && !connectionId) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [connectionId]);

  // Handle filtering logs when search term or logs change
  useEffect(() => {
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      setFilteredLogs(logs.filter(log => 
        log.message.toLowerCase().includes(lowercaseSearch) || 
        log.timestamp.toLowerCase().includes(lowercaseSearch)
      ));
    } else {
      setFilteredLogs(logs);
    }
  }, [logs, searchTerm]);

  // Scroll to bottom when new logs come in
  useEffect(() => {
    if (logsEndRef.current && !searchTerm) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredLogs]);

  const connectToLogStream = (connectionChanged) => {
    if (!connectionId) {
      console.warn("No connection ID available for logs");
      return;
    }

    // Always close any existing connection before creating a new one
    if (eventSourceRef.current) {
      console.log("Closing existing EventSource connection before creating a new one");
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      console.log(`Creating new EventSource connection for ${connectionId}`);
      const timestamp = new Date().getTime(); // Add cache-busting
      const eventSource = new EventSource(
        `${API_URL}/agentapi/agent/api/logs/stream/${connectionId}?_=${timestamp}`, 
        { withCredentials: true }
      );
      
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setConnected(true);
        // Only clear logs if we're switching to a different chat
        // NOT when we're just reconnecting to the same chat
        if(connectionChanged){
          //setLogs([]);
        } else {
          // Add a system message indicating reconnection
          setLogs(prev => [...prev, {
            timestamp: formatTime(new Date()),
            message: "Reconnected to log stream.",
            type: "system"
          }]);
        }
      };

      eventSource.addEventListener('log', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (!pausedRef.current) {
            setLogs(prev => [...prev, {
              timestamp: data.timestamp ? formatTime(data.timestamp) : formatTime(new Date()),
              message: data.message,
              type: getLogType(data.message)
            }]);
          }
        } catch (e) {
          console.error("Error parsing log data:", e);
        }
      });

      eventSource.onerror = () => {
        setConnected(false);
        // Don't clear logs on error, just add an error message
        setLogs(prev => [...prev, {
          timestamp: formatTime(new Date()),
          message: "Connection to log stream lost. Attempting to reconnect...",
          type: "error"
        }]);
        
        // Close the errored connection so we can try to reconnect
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      };
    } catch (e) {
      console.error("Error creating EventSource:", e);
      eventSourceRef.current = null;
    }
  };

  // Ensure we clean up properly when unmounting
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        console.log("Component unmounting - closing EventSource connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Modify the reconnection mechanism to ensure we don't create multiple connections
  useEffect(() => {
    let reconnectTimer;
    
    if (!connected && connectionId && !eventSourceRef.current) {
      // Try to reconnect after a delay, but only if we don't already have a connection
      reconnectTimer = setTimeout(() => {
        // Double-check we still don't have a connection before reconnecting
        if (!eventSourceRef.current) {
          console.log("Attempting to reconnect to log stream...");
          connectToLogStream(false);
        }
      }, 3000);
    }
    
    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
    };
  }, [connected, connectionId]);

  const getLogType = (message) => {
    if (!message) return "system";
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes("error ")) return "error";
    if (lowerMessage.includes("generating") || lowerMessage.includes("response")) return "ai";
    if (lowerMessage.includes("running")) return "agent";
    if (lowerMessage.includes("processing")) return "tool";
    return "system";
  };

  const formatTime = (date) => {
    if (typeof date === 'string') {
      // Parse the ISO string date (with timezone information)
      date = new Date(date);
    }
    
    // Format the time in local timezone
    return date.toLocaleTimeString();
  };

  const togglePause = () => {
    setPaused(prev => {
      const newValue = !prev;
      pausedRef.current = newValue;
      return newValue;
    });
  };

  // Get logs to display based on active tab
  const getVisibleLogs = () => {
    const logs = filteredLogs || [];
    if (activeTab === 'progress') {
      return logs.filter(log => log.type === 'ai' || log.type === 'agent' || log.type === 'tool');
    }
    return logs;
  };

  const visibleLogs = getVisibleLogs();

  // Add a function to clear logs
  const clearLogs = () => {
    setLogs([]);
    setFilteredLogs([]);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex items-center justify-between p-3 border-b">
        <h2 className="text-lg font-medium">Agent Activity Feed</h2>
        <div className="flex items-center">
          <span className={`px-2 py-1 rounded text-xs mr-2 ${connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {connected ? 'Connected' : 'Disconnected'}
          </span>
          <button 
            onClick={togglePause} 
            className="p-1 hover:bg-gray-100 rounded mr-1"
            title={paused ? "Resume" : "Pause"}
          >
            {paused ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </div>
      
      <div className="p-2">
        <div className="relative rounded-md shadow-sm">
          <input
            type="text"
            className="form-input block w-full pl-10 sm:text-sm sm:leading-5 rounded-md border border-gray-300 p-2"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      
      <div className="flex border-b">
        <button 
          className={`flex-1 py-2 text-center ${activeTab === 'progress' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
          onClick={() => setActiveTab('progress')}
        >
          Progress
        </button>
        <button 
          className={`flex-1 py-2 text-center ${activeTab === 'rawLogs' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`}
          onClick={() => setActiveTab('rawLogs')}
        >
          Raw Logs
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto bg-black p-2 text-xs font-mono relative min-h-[calc(100vh-205px)]">
        {/* Clear logs button positioned in the top-right of the console with fixed position */}
        <div className="sticky top-0 right-0 z-10 float-right ml-2 mb-1">
          <button 
            onClick={clearLogs} 
            className="p-1 bg-gray-800 hover:bg-gray-700 rounded text-white opacity-70 hover:opacity-100 transition-opacity"
            title="Clear logs"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        {visibleLogs.length === 0 ? (
          <div className="text-gray-400 text-center mt-4">
            {searchTerm ? "No logs match your search." : "Waiting for logs..."}
          </div>
        ) : (
          visibleLogs.map((log, index) => (
            <div key={index} className="mb-1">
              <span className="text-gray-400">{log.timestamp}: </span>
              <span className={`                ${log.type === 'error' ? 'text-red-400' : ''}
                ${log.type === 'ai' ? 'text-white' : ''}
                ${log.type === 'agent' ? 'text-white' : ''}
                ${log.type === 'tool' ? 'text-white' : ''}
                ${log.type === 'system' ? 'text-white' : ''}
              `}>
                {log.message}
              </span>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default LogViewer;
